import { useCallback, useMemo, useRef, useState } from "react";
import type { ComponentType } from "react";
import TipTapImage from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Trash2,
  Type,
} from "lucide-react";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  NodeViewProps,
} from "@tiptap/react";
import { useTranslations } from "next-intl";

import { cn } from "@/lib/utils";

type Alignment = "left" | "center" | "right";

export const IMAGE_MIN_WIDTH = 120;
export const IMAGE_MAX_WIDTH = 960;
export const IMAGE_MIN_HEIGHT = 80;
export const IMAGE_MAX_HEIGHT = 720;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const ALIGNMENTS: {
  value: Alignment;
  icon: ComponentType<{ className?: string }>;
  translationKey: string;
  defaultLabel: string;
}[] = [
  {
    value: "left",
    icon: AlignLeft,
    translationKey: "editor.alignLeft",
    defaultLabel: "Align left",
  },
  {
    value: "center",
    icon: AlignCenter,
    translationKey: "editor.alignCenter",
    defaultLabel: "Align center",
  },
  {
    value: "right",
    icon: AlignRight,
    translationKey: "editor.alignRight",
    defaultLabel: "Align right",
  },
];

function ResizableImageComponent({
  node,
  updateAttributes,
  editor,
  getPos,
  selected,
}: NodeViewProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const t = useTranslations("courses.admin");

  const [isCaptionDialogOpen, setIsCaptionDialogOpen] = useState(false);
  const [captionDraft, setCaptionDraft] = useState("");

  const alignment = (node.attrs.alignment as Alignment) ?? "left";
  const widthAttr = node.attrs.width as number | string | null;
  const heightAttr = node.attrs.height as number | string | null;

  const hasWidth = typeof widthAttr === "number";
  const hasHeight = typeof heightAttr === "number";

  const style = useMemo<React.CSSProperties>(() => {
    if (hasWidth) {
      return {
        width: `${widthAttr}px`,
        maxWidth: "100%",
        height: hasHeight ? `${heightAttr}px` : "auto",
      };
    }
    return { maxWidth: "100%", height: "auto" };
  }, [hasWidth, hasHeight, heightAttr, widthAttr]);

  const isInImageRow = useMemo(() => {
    try {
      const position = typeof getPos === "function" ? getPos() : getPos;
      if (typeof position !== "number") return false;
      const $pos = editor.state.doc.resolve(position);
      return $pos.parent.type.name === "imageRow";
    } catch {
      return false;
    }
  }, [editor, getPos, node]);

  const wrapperClass = useMemo(() => {
    const base = "group relative max-w-full";
    if (isInImageRow) return cn(base, "min-w-0");
    const baseBlock = "group relative max-w-full clear-both";
    if (alignment === "center")
      return cn(baseBlock, "mx-auto block w-fit float-none");
    if (alignment === "right") return cn(baseBlock, "float-right ml-4 mb-2");
    return cn(baseBlock, "float-left mr-4 mb-2");
  }, [alignment, isInImageRow]);

  const startResize = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const container = wrapperRef.current;
      const image = imageRef.current;
      if (!container || !image) return;

      const startX = event.clientX;
      const startY = event.clientY;
      const startWidth =
        typeof node.attrs.width === "number"
          ? node.attrs.width
          : image.clientWidth || image.naturalWidth || IMAGE_MIN_WIDTH;

      const viewportRect = editor.view.dom.getBoundingClientRect();
      let maxWidthSource = viewportRect.width
        ? viewportRect.width - 48
        : IMAGE_MAX_WIDTH;

      const cardWrapper = container.closest<HTMLElement>(
        '[data-layout-block][data-layout-variant="card"]',
      );
      if (cardWrapper?.clientWidth) {
        const innerWidth = Math.max(
          cardWrapper.clientWidth - 32,
          IMAGE_MIN_WIDTH,
        );
        maxWidthSource = Math.min(maxWidthSource, innerWidth);
      }

      const maxWidth = clamp(maxWidthSource, IMAGE_MIN_WIDTH, IMAGE_MAX_WIDTH);

      const move = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault();
        const deltaX = moveEvent.clientX - startX;
        const deltaY = moveEvent.clientY - startY;

        const delta = Math.abs(deltaX) > Math.abs(deltaY) ? deltaX : deltaY;
        const nextWidth = clamp(startWidth + delta, IMAGE_MIN_WIDTH, maxWidth);

        updateAttributes({
          width: Math.round(nextWidth),
          height: null,
        });
      };

      const stop = () => {
        window.removeEventListener("mousemove", move);
        window.removeEventListener("mouseup", stop);
      };

      window.addEventListener("mousemove", move, { passive: false });
      window.addEventListener("mouseup", stop, { once: true });
      const position = typeof getPos === "function" ? getPos() : getPos;
      if (typeof position === "number") {
        editor.commands.setNodeSelection(position);
      }
    },
    [editor, getPos, node.attrs.width, updateAttributes],
  );

  const handleAlign = useCallback(
    (value: Alignment) => {
      updateAttributes({ alignment: value });

      const position = typeof getPos === "function" ? getPos() : getPos;
      if (typeof position === "number") {
        const { state, view } = editor;
        const { doc } = state;
        const $pos = doc.resolve(position);

        // 图片行内的对齐作用于整行
        if ($pos.parent.type.name === "imageRow") {
          const parent = $pos.parent;
          const parentPos = $pos.before($pos.depth);
          if (parent.attrs.align !== value) {
            view.dispatch(
              state.tr.setNodeMarkup(parentPos, undefined, {
                ...parent.attrs,
                align: value,
              }),
            );
          }
        }

        // 仅在居中卡片(CC: layoutBlock variant=card 且 cardAlign=center)中自动调整相邻段落的对齐方式
        for (let depth = $pos.depth; depth > 0; depth -= 1) {
          const parent = $pos.node(depth);
          if (parent.type.name !== "layoutBlock") continue;
          const parentAttrs = parent.attrs as {
            variant?: string;
            cardAlign?: string;
          };
          if (
            parentAttrs.variant !== "card" ||
            parentAttrs.cardAlign !== "center"
          )
            break;

          const indexInParent = $pos.index(depth);
          const parentStart = $pos.before(depth);

          const mapAlignToTextAlign = (
            align: Alignment,
          ): "left" | "center" | "right" => {
            if (align === "right") return "right";
            if (align === "center") return "center";
            return "left";
          };

          const textAlign = mapAlignToTextAlign(value);
          let tr = state.tr;

          const adjustSibling = (siblingIndex: number) => {
            if (siblingIndex < 0 || siblingIndex >= parent.childCount) return;
            const sibling = parent.child(siblingIndex);
            if (sibling.type.name !== "paragraph") return;

            let offset = parentStart + 1; // 进入 layoutBlock 内容区域
            for (let i = 0; i < siblingIndex; i += 1) {
              offset += parent.child(i).nodeSize;
            }

            tr = tr.setNodeMarkup(offset, sibling.type, {
              ...sibling.attrs,
              textAlign,
            });
          };

          if (value === "right") {
            // 右图左文：优先调整左侧段落
            adjustSibling(indexInParent - 1);
          } else if (value === "left") {
            // 左图右文：优先调整右侧段落
            adjustSibling(indexInParent + 1);
          } else if (value === "center") {
            // 图片居中时，尽量让前后段落都居中
            adjustSibling(indexInParent - 1);
            adjustSibling(indexInParent + 1);
          }

          if (tr.docChanged) {
            view.dispatch(tr);
          }
          break;
        }
      }

      editor.commands.focus();
    },
    [editor, getPos, updateAttributes],
  );

  const handleDelete = useCallback(() => {
    const position = typeof getPos === "function" ? getPos() : getPos;
    if (typeof position === "number") {
      editor.chain().setNodeSelection(position).deleteSelection().focus().run();
    }
  }, [editor, getPos]);

  const handleOpenCaptionDialog = useCallback(() => {
    setCaptionDraft("");
    setIsCaptionDialogOpen(true);
  }, []);

  const handleConfirmCaption = useCallback(() => {
    const value = captionDraft.trim();
    if (!value) {
      setIsCaptionDialogOpen(false);
      return;
    }

    updateAttributes({ caption: value });
    setIsCaptionDialogOpen(false);
  }, [captionDraft, updateAttributes]);

  const handleMove = useCallback(
    (direction: "up" | "down") => {
      const position = typeof getPos === "function" ? getPos() : getPos;
      if (typeof position !== "number") return;

      const { state, view } = editor;
      let nodeAtPos = state.doc.nodeAt(position);
      if (!nodeAtPos) return;

      let from = position;
      const $pos = state.doc.resolve(from);
      if (
        $pos.parent.type.name === "imageRow" &&
        $pos.parent.childCount === 1
      ) {
        const rowFrom = $pos.before($pos.depth);
        const rowNode = state.doc.nodeAt(rowFrom);
        if (rowNode) {
          from = rowFrom;
          nodeAtPos = rowNode;
        }
      }

      const to = from + nodeAtPos.nodeSize;
      let tr = state.tr;

      if (direction === "down") {
        const nextNode = state.doc.nodeAt(to);
        if (!nextNode) return;
        const nextSize = nextNode.nodeSize;
        const slice = state.doc.slice(to, to + nextSize);
        tr = tr.delete(to, to + nextSize).insert(from, slice.content);
      } else {
        const $pos = state.doc.resolve(from);
        const parent = $pos.parent;
        const index = $pos.index();
        if (index === 0) return;
        const prevNode = parent.child(index - 1);
        const prevFrom = from - prevNode.nodeSize;
        const prevTo = from;
        const nextFrom = prevTo;
        const currentNode = state.doc.nodeAt(nextFrom);
        if (!currentNode) return;
        const currentSize = currentNode.nodeSize;
        const slice = state.doc.slice(nextFrom, nextFrom + currentSize);
        tr = tr
          .delete(nextFrom, nextFrom + currentSize)
          .insert(prevFrom, slice.content);
      }

      view.dispatch(tr.scrollIntoView());
      view.focus();
    },
    [editor, getPos],
  );

  const handleImageContextMenu = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      event.preventDefault();
    },
    [],
  );

  const handleDragStart = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const position = typeof getPos === "function" ? getPos() : getPos;
      if (typeof position === "number") {
        editor.commands.setNodeSelection(position);
      }
      if (imageRef.current) {
        event.dataTransfer?.setDragImage(
          imageRef.current,
          imageRef.current.width / 2,
          imageRef.current.height / 2,
        );
      }
    },
    [editor, getPos],
  );

  const handleClick = useCallback(() => {
    const position = typeof getPos === "function" ? getPos() : getPos;
    if (typeof position === "number") {
      editor.commands.setNodeSelection(position);
    }
  }, [editor, getPos]);

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={wrapperClass}
      data-drag-handle
      draggable
      onClick={handleClick}
      onDragStart={handleDragStart}
    >
      <figure className="relative inline-block max-w-full">
        <img
          ref={imageRef}
          src={node.attrs.src as string}
          alt={node.attrs.alt ?? ""}
          title={node.attrs.title ?? ""}
          style={style}
          className={cn(
            "max-w-full rounded-md border border-slate-200 bg-white object-contain shadow-sm dark:border-slate-700 dark:bg-slate-900",
            selected ? "ring-2 ring-emerald-400" : "ring-0",
          )}
          draggable={false}
          onContextMenu={handleImageContextMenu}
        />
        {node.attrs.caption && (
          <figcaption className="mt-1 text-xs text-slate-500 dark:text-slate-400 text-center">
            {node.attrs.caption}
          </figcaption>
        )}
        <div className="pointer-events-none absolute inset-x-2 top-2 flex justify-between opacity-0 transition-opacity group-hover:opacity-100">
          <div className="pointer-events-auto flex items-center gap-1 rounded-md border border-slate-200 bg-white/90 px-2 py-1 text-xs font-medium shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80">
            {ALIGNMENTS.map(
              ({ value, icon: Icon, translationKey, defaultLabel }) => (
                <button
                  key={value}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleAlign(value);
                  }}
                  className={cn(
                    "rounded p-1 text-slate-600 transition hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:text-slate-300 dark:hover:bg-emerald-500/20",
                    alignment === value
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-600/40 dark:text-emerald-100"
                      : "bg-transparent",
                  )}
                  title={t(translationKey, { defaultValue: defaultLabel })}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ),
            )}
          </div>
          <div className="pointer-events-auto ml-2 flex items-center gap-1 rounded-md border border-slate-200 bg-white/90 px-1 py-1 text-xs font-medium shadow-sm backdrop-blur dark:border-slate-700 dark:bg-slate-900/80">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMove("up");
              }}
              className="rounded p-1 text-slate-600 transition hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:text-slate-300 dark:hover:bg-emerald-500/20"
              title={t("editor.moveImageUp", { defaultValue: "Move image up" })}
            >
              <ArrowUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleMove("down");
              }}
              className="rounded p-1 text-slate-600 transition hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:text-slate-300 dark:hover:bg-emerald-500/20"
              title={t("editor.moveImageDown", {
                defaultValue: "Move image down",
              })}
            >
              <ArrowDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleOpenCaptionDialog();
              }}
              className="rounded p-1 text-slate-600 transition hover:bg-emerald-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 dark:text-slate-300 dark:hover:bg-emerald-500/20"
              title={t("editor.addCaption", {
                defaultValue: "Add caption below",
              })}
            >
              <Type className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete();
              }}
              className="rounded p-1 text-slate-600 transition hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300 dark:text-slate-300 dark:hover:bg-rose-500/20"
              title="删除图片"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        <span
          role="presentation"
          onMouseDown={startResize}
          className="pointer-events-auto absolute -bottom-1 -right-1 h-4 w-4 cursor-se-resize rounded-sm border border-emerald-500 bg-emerald-400 shadow"
        />
      </figure>
      {isCaptionDialogOpen && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/30"
          onClick={() => setIsCaptionDialogOpen(false)}
        >
          <div
            className="w-full max-w-xs rounded-xl bg-white/95 p-4 shadow-lg ring-1 ring-emerald-100 dark:bg-slate-900/95 dark:ring-emerald-500/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {t("prompts.caption", { defaultValue: "Enter image caption" })}
            </div>
            <input
              type="text"
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value)}
              className="mt-3 w-full rounded-md border border-emerald-100 bg-emerald-50/40 px-3 py-2 text-sm text-slate-900 shadow-sm outline-none ring-0 placeholder:text-slate-400 focus:border-emerald-300 focus:ring-2 focus:ring-emerald-200 dark:border-emerald-700/60 dark:bg-slate-800/80 dark:text-slate-50 dark:placeholder:text-slate-500 dark:focus:border-emerald-400 dark:focus:ring-emerald-500/60"
              placeholder={t("prompts.caption", {
                defaultValue: "Enter image caption",
              })}
            />
            <div className="mt-4 flex justify-end gap-2 text-xs">
              <button
                type="button"
                onClick={() => setIsCaptionDialogOpen(false)}
                className="rounded-md border border-slate-200 bg-white/80 px-3 py-1 text-slate-600 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                {t("buttons.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={handleConfirmCaption}
                className="rounded-md bg-emerald-500 px-3 py-1 font-medium text-white shadow-sm transition hover:bg-emerald-600"
              >
                {t("buttons.save", { defaultValue: "Save" })}
              </button>
            </div>
          </div>
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const ResizableImageNode = TipTapImage.extend({
  inline: false,
  group: "block",
  addAttributes(this: { parent?: () => Record<string, unknown> }) {
    const baseAttributes =
      (typeof this.parent === "function" ? this.parent() : {}) ?? {};

    return {
      ...baseAttributes,
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const data = element.getAttribute("data-width");
          if (data) return Number(data);
          const width = element.style.width?.replace("px", "");
          return width ? Number(width) : null;
        },
        renderHTML: (attributes: { width?: number | string | null }) =>
          attributes.width ? { "data-width": attributes.width } : {},
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const data = element.getAttribute("data-height");
          if (data) return Number(data);
          const height = element.style.height?.replace("px", "");
          return height ? Number(height) : null;
        },
        renderHTML: (attributes: { height?: number | string | null }) =>
          attributes.height ? { "data-height": attributes.height } : {},
      },
      alignment: {
        default: "left",
        parseHTML: (element: HTMLElement) => {
          const dataAlign = element.getAttribute("data-alignment");
          if (
            dataAlign === "left" ||
            dataAlign === "center" ||
            dataAlign === "right"
          )
            return dataAlign;

          const classAttr = element.getAttribute("class") ?? "";
          const classTokens = classAttr.split(/\s+/).filter(Boolean);
          const hasClass = (...targets: string[]) =>
            classTokens.some((cls) => targets.includes(cls));
          if (
            hasClass(
              "tm-align-center",
              "text-center",
              "mx-auto",
              "justify-center",
            )
          )
            return "center";
          if (
            hasClass(
              "tm-align-right",
              "text-right",
              "float-right",
              "justify-end",
            )
          )
            return "right";
          if (
            hasClass(
              "tm-align-left",
              "text-left",
              "float-left",
              "justify-start",
            )
          )
            return "left";

          const styleAttr = element.getAttribute("style")?.toLowerCase() ?? "";
          const styleNormalized = styleAttr.replace(/\s+/g, "");
          if (styleNormalized.includes("float:right")) return "right";
          if (styleNormalized.includes("float:left")) return "left";

          const marginLeftAuto = styleNormalized.includes("margin-left:auto");
          const marginRightAuto = styleNormalized.includes("margin-right:auto");
          if (marginLeftAuto && marginRightAuto) return "center";

          if (styleNormalized.includes("text-align:center")) return "center";
          if (styleNormalized.includes("text-align:right")) return "right";

          const legacyAlign = element.getAttribute("align");
          if (
            legacyAlign === "center" ||
            legacyAlign === "right" ||
            legacyAlign === "left"
          ) {
            return legacyAlign as Alignment;
          }

          return "left";
        },
        renderHTML: (attributes: {
          alignment?: Alignment | string | null;
          class?: string;
        }) => {
          const alignment =
            (attributes.alignment as Alignment | undefined) ?? "left";
          const alignmentClass =
            alignment === "center"
              ? "tm-align-center"
              : alignment === "right"
                ? "tm-align-right"
                : "tm-align-left";
          const className = cn(
            attributes.class ?? "",
            "tm-resizable-image",
            alignmentClass,
          );

          return {
            "data-alignment": alignment,
            class: className,
          };
        },
      },
      caption: {
        default: null,
        parseHTML: (element: HTMLElement) => {
          const data = element.getAttribute("data-caption");
          if (data) return data;

          const parent = element.parentElement;
          if (parent && parent.tagName === "FIGURE") {
            const figcaption = parent.querySelector("figcaption");
            if (figcaption) return figcaption.textContent ?? null;
          }

          return null;
        },
        renderHTML: (attributes: { caption?: string | null }) =>
          attributes.caption ? { "data-caption": attributes.caption } : {},
      },
    };
  },

  renderHTML({
    node,
    HTMLAttributes,
  }: {
    node: { attrs?: { alignment?: Alignment; caption?: string | null } };
    HTMLAttributes: Record<string, unknown>;
  }) {
    const attrs = { ...HTMLAttributes } as Record<string, unknown>;
    const alignment =
      (node?.attrs?.alignment as Alignment | undefined) ?? "left";
    const caption = node?.attrs?.caption ?? null;
    delete (attrs as { alignment?: unknown }).alignment;

    const width = attrs.width;
    const height = attrs.height;

    const styleParts: string[] = [];
    if (width)
      styleParts.push(
        `width: ${typeof width === "number" ? `${width}px` : width}`,
      );
    if (height)
      styleParts.push(
        `height: ${typeof height === "number" ? `${height}px` : height}`,
      );

    const imgAttrs: Record<string, unknown> = {
      ...attrs,
    };

    if (styleParts.length) {
      imgAttrs.style = styleParts.join("; ");
    }
    if (width) imgAttrs["data-width"] = width;
    if (height) imgAttrs["data-height"] = height;

    const alignmentClass =
      alignment === "center"
        ? "tm-align-center"
        : alignment === "right"
          ? "tm-align-right"
          : "tm-align-left";

    if (!caption) {
      const className = cn(
        imgAttrs.class as string | undefined,
        "tm-resizable-image",
        alignmentClass,
      );
      const finalAttrs: Record<string, unknown> = {
        ...imgAttrs,
        class: className,
        "data-alignment": alignment,
      };

      return ["img", mergeAttributes(this.options.HTMLAttributes, finalAttrs)];
    }

    const figureStyleParts: string[] = [];
    if (alignment === "center") {
      figureStyleParts.push("margin-left: auto", "margin-right: auto");
    } else if (alignment === "right") {
      figureStyleParts.push("margin-left: auto");
    }

    const figureAttrs: Record<string, unknown> = {
      class: cn("inline-block", alignmentClass),
      "data-alignment": alignment,
    };
    if (figureStyleParts.length) {
      figureAttrs.style = figureStyleParts.join("; ");
    }

    const imgFinalAttrs = mergeAttributes(this.options.HTMLAttributes, {
      ...imgAttrs,
      class: cn(imgAttrs.class as string | undefined, "tm-resizable-image"),
    });

    return [
      "figure",
      figureAttrs,
      ["img", imgFinalAttrs],
      [
        "figcaption",
        {
          class: "mt-1 text-xs text-slate-500 dark:text-slate-400 text-center",
        },
        caption,
      ],
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageComponent);
  },

  draggable: true,
});
